import { PROJECTS_PATHS } from "src/pages/projects/constants";
import { useLocation, useMatch } from "src/router";

const PROJECT_TABS = ["overview", "tasks", "attachments"] as const;

export type ProjectTab = (typeof PROJECT_TABS)[number];

/** Kept in `location.state` on a page opened from a project tab, so its project crumb returns there. */
type ProjectTabOrigin = { projectTab: ProjectTab };

function isProjectTab(value: unknown): value is ProjectTab {
  return (PROJECT_TABS as readonly unknown[]).includes(value);
}

export function projectTabPath(projectId: string, tab: ProjectTab): string {
  return `${PROJECTS_PATHS.project(projectId)}/${tab}`;
}

/** The project tab this page was opened from. A direct link has none. */
export function useProjectTabOrigin(): ProjectTab | undefined {
  const state = useLocation().state as Partial<ProjectTabOrigin> | null;
  return isProjectTab(state?.projectTab) ? state.projectTab : undefined;
}

/** The project tab the URL is on, or undefined off a project's tabs, as on a task's page. */
export function useCurrentProjectTab(): ProjectTab | undefined {
  const tab = useMatch(`${PROJECTS_PATHS.project(":projectId")}/:tab`)?.params
    .tab;
  return isProjectTab(tab) ? tab : undefined;
}

/** State for a link to a page under a project: the tab it leaves, or the tab the current page was opened from. */
export function useProjectTabOriginState(): ProjectTabOrigin | undefined {
  const currentTab = useCurrentProjectTab();
  const origin = useProjectTabOrigin();
  const projectTab = currentTab ?? origin;
  return projectTab ? { projectTab } : undefined;
}
